import React, { useState, useEffect } from 'react';
import { getTokenForRequest } from '../../utils/apiAuth';
import './QuestionControl.css';

/**
 * QuestionControl Component
 * Provides methods for socrates-question.js to call React functionality
 * Handles template question generation and duplicate checking
 */
const QuestionControl = ({ questionInstance = null }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [generationStatus, setGenerationStatus] = useState(null);
  const [duplicateStatus, setDuplicateStatus] = useState(null);

  /**
   * Initialize template question generation
   * Called by socrates-question.js
   */
  const initTemplateQuestionGeneration = async (question = questionInstance) => {
    if (!question) {
      console.warn('No question instance provided for template generation');
      return;
    }

    setIsGenerating(true);
    setGenerationStatus('Generating template question...');

    try {
      // API call for template generation
      const response = await fetch('/api/template-question/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({
          questionId: question.id || question.ID,
          questionType: question.type || question.QuestionType,
          difficulty: question.difficulty || question.Difficulty
        })
      });

      if (response.ok) {
        const result = await response.json();
        setGenerationStatus('Template generated successfully!');
        return result;
      } else {
        throw new Error('Failed to generate template');
      }
    } catch (error) {
      console.error('Error generating template:', error);
      setGenerationStatus('Error generating template: ' + error.message);
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * Check for duplicate questions
   * Called by socrates-question.js
   * Matches Angular functionality from question-control.js
   */
  const checkSubLayerDuplicateQuestion = async (event) => {
    // Ensure jQuery is available
    if (typeof $ === 'undefined') {
      console.error('jQuery is required for duplicate checking');
      alert('Failed to check duplicate question: jQuery not available');
      return;
    }

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Extract data from DOM (matching Angular lines 151-175)
    // Handle both Angular event format ({event: {target: ...}}) and direct event
    const eventTarget = event?.event?.target || event?.target || event;
    const $eventTarget = $(eventTarget);
    const questionDom = $eventTarget.parents('.subLayer');
    
    if (questionDom.length === 0) {
      console.warn('Could not find .subLayer parent element');
      alert('Failed to check duplicate question: Question element not found');
      return;
    }

    const questionId = questionDom.find('.subQuestion').attr('id');
    const statement = questionDom.find('.statement').html();
    
    const topicIDs = [];
    const mainTopic = questionDom.find(".mainTopic").data('topicID');
    
    if (parseInt(mainTopic) > 0) {
      topicIDs.push(mainTopic);
    }
    
    questionDom.find(".diagnosticsTopic").each(function(index, topic) {
      const topicID = $(topic).data('topicID');
      if (parseInt(topicID) > 0) {
        topicIDs.push(topicID);
      }
    });

    const token = getTokenForRequest();

    // Show spinner (matching Angular lines 177-178)
    $(".contentspinner").show();
    $(".spinnerinfo").html("Checking, this could take more than 30 seconds to finish.");

    try {
      // Get duplicate checking server URL
      const duplicateCheckingServer = typeof window.duplicate_checking_server !== 'undefined'
        ? window.duplicate_checking_server
        : 'https://demo.3elogic.com.sg/learningantsalgo';

      // Call API (matching Angular API call)
      const response = await fetch(duplicateCheckingServer + '/duplicatecheck/check_duplicate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        },
        body: JSON.stringify({
          token: token,
          statement: statement,
          topicIDs: topicIDs
        })
      });

      $(".contentspinner").hide();

      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }

      let data = await response.json();
      
      // Handle different response formats (some APIs wrap the array)
      if (data && !Array.isArray(data)) {
        if (data.data && Array.isArray(data.data)) {
          data = data.data;
        } else if (data.d && Array.isArray(data.d)) {
          data = data.d;
        } else if (data.result && Array.isArray(data.result)) {
          data = data.result;
        } else {
          // If it's not an array, treat as empty
          data = [];
        }
      }
      
      // Ensure data is an array
      if (!Array.isArray(data)) {
        data = [];
      }
      
      // Handle response (matching Angular lines 186-253)
      if (data && data.length > 0) {
        let subLayerDuplicateQuestions = [];

        // Filter out current question if questionId exists (matching Angular lines 190-212)
        if (parseInt(questionId) > 0) {
          data.forEach((question) => {
            if (parseInt(questionId) !== question.id) {
              subLayerDuplicateQuestions.push(question);
            }
          });
        } else {
          subLayerDuplicateQuestions = data;
        }

        // Show modal or alert (matching Angular lines 216-252)
        if (subLayerDuplicateQuestions.length > 0) {
          // Ensure modal exists in DOM
          let $modal = $("#sublayerDuplicateQuestion");
          if ($modal.length === 0) {
            // Create modal if it doesn't exist (matching Angular template structure)
            $modal = $(`
              <div class="modal fade" id="sublayerDuplicateQuestion" style="margin-top:80px">
                <div class="modal-dialog" style="width:80%">
                  <div class="modal-content">
                    <div class="modal-header">
                      <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                        <span aria-hidden="true">&times;</span>
                      </button>
                      <h4 class="modal-title">We have found these similar questions</h4>
                    </div>
                    <div class="modal-body"></div>
                    <div class="modal-footer"></div>
                  </div>
                </div>
              </div>
            `);
            $('body').append($modal);
          }
          
          // Populate modal with questions (matching Angular template structure)
          const modalBody = $modal.find('.modal-body');
          
          // Clear existing content and create new structure
          modalBody.html(`
            <div class="row">
              ${subLayerDuplicateQuestions.slice(0, 5).map((question, index) => `
                <div class="col-sm-12" ${index < 4 ? 'style="margin-bottom: 15px;"' : ''}>
                  <a target="_blank" href="#Questions/View/${question.id}">
                    <div class="col-sm-1">${question.id}</div>
                    <div class="col-sm-11">${question.statement || ''}</div>
                  </a>
                </div>
              `).join('')}
            </div>
          `);

          // Show modal (matching Angular lines 220-223)
          $modal.modal({
            backdrop: 'static',
            keyboard: false
          });

          // Refresh page on closing modal (matching Angular lines 226-229)
          $modal.off('hidden.bs.modal').on('hidden.bs.modal', function (e) {
            window.location.reload();
          });

          // Render MathJax (matching Angular lines 232-236)
          if (typeof MathJax !== 'undefined' && MathJax.Hub) {
            setTimeout(() => {
              MathJax.Hub.Queue(["Typeset", MathJax.Hub]);
            }, 100);
          }
        } else {
          alert('No duplicate question found');
        }
      } else {
        alert('No duplicate question found');
      }

      // Save duplicate search result if questionId > 0 (matching Angular lines 257-261)
      if (parseInt(questionId) > 0 && data) {
        try {
          // Get userID from cookie
          const CookieUtilities = window.CookieUtilities || (typeof CookieUtilities !== 'undefined' ? CookieUtilities : null);
          let userID = '';
          
          if (CookieUtilities) {
            const ch = new CookieUtilities();
            userID = ch.getCookie("userID");
          }

          // Call SaveDuplicateSearchResult API if available
          const saveToken = getTokenForRequest();
          if (window.api && typeof window.api.SaveDuplicateSearchResult === 'function') {
            window.api.SaveDuplicateSearchResult(saveToken, userID, questionId, JSON.stringify(data));
          } else {
            // Fallback: direct API call
            const root = typeof window.root !== 'undefined' ? window.root : '';
            const saveResponse = await fetch(root + 'webservices/Question.svc/saveDuplicateSearchResult', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                token: typeof window.encodeToken === 'function' ? window.encodeToken(saveToken) : saveToken,
                userId: userID,
                questionId: questionId,
                searchResult: JSON.stringify(data)
              }),
              credentials: 'include',
            });
          }
        } catch (saveError) {
          console.warn('Failed to save duplicate search result:', saveError);
          // Don't show error to user as this is not critical
        }
      }

      return data;
    } catch (error) {
      $(".contentspinner").hide();
      console.error('Error checking duplicates:', error);
      alert('Failed to check duplicate question');
      return null;
    }
  };

  // Expose methods globally for socrates-question.js
  useEffect(() => {
    window.QuestionControl = {
      initTemplateQuestionGeneration,
      checkSubLayerDuplicateQuestion
    };

    return () => {
      delete window.QuestionControl;
    };
  }, [questionInstance]);

  return (
    <div className="question-control">
      {/* Status Messages */}
      {generationStatus && (
        <div className={`status-message ${isGenerating ? 'info' : 'success'}`}>
          {generationStatus}
        </div>
      )}
      
      {duplicateStatus && (
        <div className={`status-message ${isCheckingDuplicate ? 'info' : 'success'}`}>
          {duplicateStatus}
        </div>
      )}
    </div>
  );
};

export default QuestionControl;
